import { callApi } from './apiCall';

/**
 * resolveNextTaskId({ practiceId, leadId })
 *
 * The New-lead table's row gives next_task_title / next_task_due_at but no
 * task id (BE contract intentionally keeps the leads list lightweight — see
 * NextTaskCell.jsx). Clicking the Next Task cell needs the real id to open
 * TaskViewer, so this re-fetches the lead's open tasks — same endpoint
 * LeadTaskList.jsx already uses (`GET /practices/<id>/tasks/?lead=<id>&
 * status__in=pending,in_progress&ordering=due_at`) — and picks the first
 * top-level task with a due date, matching the same "earliest-due open
 * top-level task" definition next_task_title/next_task_due_at use.
 *
 * `parent_task__isnull=true` is sent server-side (confirmed supported —
 * TemplateLibrary.jsx uses the same param) so the BE does the filtering; the
 * client-side filter below is belt-and-braces in case a BE version predates
 * that param or returns extra rows.
 *
 * Never throws — returns null on any error (missing ids, non-200, network
 * failure) or when no matching task exists, and logs failures via
 * console.error so they're visible without breaking the click flow.
 *
 * @param {{ practiceId: string|number, leadId: string|number, title?: string }} params
 * @returns {Promise<string|number|null>}
 */
let warnedTitleMismatch = false;
const sameTitle = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

export const resolveNextTaskId = async ({ practiceId, leadId, title = null }) => {
    if (!practiceId || !leadId) return null;

    try {
        // Order mirrors the backend annotation's tie-break (due_at, created_at, pk) so many tasks
        // sharing one due_at resolve to the SAME task the Next Task cell shows.
        const res = await callApi(
            `/api/v1/practices/${practiceId}/tasks/?lead=${leadId}&status__in=pending,in_progress&ordering=due_at,created_at,id&parent_task__isnull=true&limit=20`
        );
        if (res?.status !== 200) return null;

        const results = res.data?.results || [];
        // Belt-and-braces: keep only top-level tasks with a due date, even
        // though the query above already asks the BE to do both.
        const candidates = results.filter(
            (task) => !task?.parent_task_id && !task?.parent_task && task?.due_at
        );
        const first = candidates[0];
        if (!first) return null;

        // Old-backend safety net: if the tie-break still picked a task whose title is not the one the
        // row shows, prefer the first candidate that matches the row's title.
        if (title && !sameTitle(first.title, title)) {
            const match = candidates.find((task) => sameTitle(task.title, title));
            if (match) {
                if (!warnedTitleMismatch) {
                    warnedTitleMismatch = true;
                    // eslint-disable-next-line no-console
                    console.warn('[resolveNextTaskId] resolved task title differs from the row\'s next_task_title; using the title match', { resolved: first.id, matched: match.id });
                }
                return match.id;
            }
        }
        return first.id ?? null;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[resolveNextTaskId] failed to resolve next task id', error);
        return null;
    }
};

export const __resetResolveNextTaskIdWarning = () => { warnedTitleMismatch = false; };

export default resolveNextTaskId;
